import { InlineMathIndentation as TiptapMathIndentationExtension } from "@corca/tiptap";
import { isNodeSelection } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";

import { lazyPluginKey } from "./tiptap-expression/TiptapInlineExpression";
import { nodeIsExpression } from "./tiptap-expression/utils";
import {
  findIndentationGroups,
  getGroupIndentations,
  createOperatorMetricsResolver,
  isMathIndentationDisabled,
  mapIndentationGroupToAlignment,
} from "./utils/indentation";

import type { IndentationGroup } from "./utils/indentation";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Alignment, InlineNodeAttrs } from "@corca/tiptap";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mathIndentation: {
      /**
       * Set mathIndentation attribute on paragraph
       * @example editor.commands.setMathIndentation(true)
       */
      setMathIndentation: (mathIndentation: boolean) => ReturnType;
    };
  }
}

type AlignmentOptions = {
  doc: DocumentModel;
};

type IndentationRange = {
  minPos: number;
  maxPos: number;
};

type AlignmentPluginState = {
  range: IndentationRange;
  version: number;
};

const alignmentPluginKey = new PluginKey<AlignmentPluginState>("inlineMathIndentation");

function getFirstLineIndent(view: EditorView, paragraph: PMNode, paragraphPos: number): number {
  if (!paragraph.attrs.firstLineIndent) {
    return 0;
  }

  const { node } = view.domAtPos(paragraphPos + 1);
  return node instanceof HTMLElement ? parseFloat(getComputedStyle(node).textIndent) || 0 : 0;
}

function applyIndentation(
  view: EditorView,
  doc: DocumentModel,
  range: { minPos: number; maxPos: number },
) {
  if (isMathIndentationDisabled()) {
    return;
  }

  let { minPos, maxPos } = range;

  view.state.doc.nodesBetween(range.minPos, range.maxPos, (node, pos) => {
    if (node && node.type.name === "paragraph") {
      minPos = Math.min(range.minPos, pos);
      maxPos = Math.max(range.maxPos, pos + node.nodeSize);
    }
  });

  const indentationGroups: { alignment: Alignment; groups: IndentationGroup[] }[] = [];

  view.state.doc.nodesBetween(minPos, maxPos, (node, pos) => {
    if (node && node.type.name === "paragraph" && node.attrs.mathIndentation) {
      const groups = findIndentationGroups(node, pos, doc, getFirstLineIndent(view, node, pos));
      indentationGroups.push({
        groups,
        alignment: (node.attrs.alignment ?? "left") as Alignment,
      });
    }
  });

  const indentedNodeIds = new Map<string, number>();
  const nodeIdsInGroups = new Set<string>();
  const resolveOperatorMetrics = createOperatorMetricsResolver(doc, view);

  for (const { alignment, groups } of indentationGroups) {
    for (const group of groups) {
      const alignedGroup = mapIndentationGroupToAlignment(
        { alignment, group },
        resolveOperatorMetrics,
      );

      for (const { nodeId } of alignedGroup.targets) {
        nodeIdsInGroups.add(nodeId);
      }

      const groupIndentations = getGroupIndentations(
        { alignment, group: alignedGroup },
        resolveOperatorMetrics,
      );

      for (const [nodeId, indentation] of groupIndentations.entries()) {
        indentedNodeIds.set(nodeId, indentation);
      }
    }
  }

  const { tr } = view.state;

  view.state.doc.nodesBetween(minPos, maxPos, (node, pos) => {
    if (node.type.name !== "inlineExpression") {
      return;
    }

    const { nodeId, indentation: currentIndentation } = node.attrs as InlineNodeAttrs;
    const expectedIndentation = indentedNodeIds.get(nodeId);

    const shouldUpdateIndentation =
      expectedIndentation !== undefined && currentIndentation !== expectedIndentation;
    const shouldResetIndentation = currentIndentation && !nodeIdsInGroups.has(nodeId);

    if (shouldUpdateIndentation) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, indentation: expectedIndentation });
    } else if (shouldResetIndentation) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, indentation: null });
    }
  });

  if (tr.steps.length > 0) {
    tr.setMeta("addToHistory", false);
    const { selection } = view.state;
    if (isNodeSelection(selection) && nodeIsExpression(selection.node)) {
      tr.setSelection(NodeSelection.create(tr.doc, selection.from));
    }
    view.dispatch(tr);
  }
}

export const InlineMathIndentation = TiptapMathIndentationExtension.extend<AlignmentOptions>({
  addProseMirrorPlugins() {
    const { doc } = this.options;
    let pendingUpdate: number | null = null;

    const scheduleIndentationUpdate = (
      view: EditorView,
      range: { minPos: number; maxPos: number },
    ) => {
      if (pendingUpdate !== null) {
        cancelAnimationFrame(pendingUpdate);
      }

      pendingUpdate = requestAnimationFrame(() => {
        pendingUpdate = null;
        applyIndentation(view, doc, range);
      });
    };

    return [
      new Plugin({
        key: alignmentPluginKey,

        state: {
          init(): AlignmentPluginState {
            return {
              range: {
                minPos: Infinity,
                maxPos: -Infinity,
              },
              version: 0,
            };
          },
          apply(tr, value): AlignmentPluginState {
            const meta = tr.getMeta(lazyPluginKey) as { addEager?: number[] } | undefined;
            const addEager = meta?.addEager ?? [];
            const previousVersion = value?.version ?? 0;

            let minPos = Infinity;
            let maxPos = -Infinity;
            let touched = false;

            tr.mapping.maps.forEach((map) => {
              touched = true;
              map.forEach((_, __, newStart, newEnd) => {
                minPos = Math.min(minPos, newStart);
                maxPos = Math.max(maxPos, newEnd);
              });
            });

            for (const pos of addEager) {
              touched = true;
              minPos = Math.min(minPos, pos);
              maxPos = Math.max(maxPos, pos + 1);
            }

            if (!touched) {
              if (tr.docChanged) {
                return {
                  range: {
                    minPos: 0,
                    maxPos: tr.doc.content.size,
                  },
                  version: previousVersion + 1,
                };
              }

              return value;
            }

            return {
              range: {
                minPos,
                maxPos: Math.max(0, Math.min(maxPos, tr.doc.content.size)),
              },
              version: previousVersion + 1,
            };
          },
        },

        view() {
          let isFirstUpdate = true;
          let lastScheduledRange: IndentationRange | null = null;
          let lastScheduledVersion: number | null = null;

          return {
            update: (updatedView) => {
              const { state } = updatedView;
              const pluginState = alignmentPluginKey.getState(state);
              const { range, version } = pluginState ?? {};

              if (isFirstUpdate) {
                isFirstUpdate = false;
                lastScheduledRange = { minPos: 0, maxPos: state.doc.content.size };
                lastScheduledVersion = version ?? null;
                scheduleIndentationUpdate(updatedView, lastScheduledRange);
                return;
              }

              if (!range || range.minPos > range.maxPos) {
                lastScheduledRange = null;
                lastScheduledVersion = version ?? null;
                return;
              }

              const rangeChanged =
                !lastScheduledRange ||
                lastScheduledRange.minPos !== range.minPos ||
                lastScheduledRange.maxPos !== range.maxPos;
              const versionChanged =
                typeof version === "number" && version !== lastScheduledVersion;

              if (rangeChanged || versionChanged) {
                lastScheduledRange = { minPos: range.minPos, maxPos: range.maxPos };
                lastScheduledVersion = version ?? null;
                scheduleIndentationUpdate(updatedView, lastScheduledRange);
              }
            },

            destroy: () => {
              if (pendingUpdate !== null) {
                cancelAnimationFrame(pendingUpdate);
              }
            },
          };
        },
      }),
      new Plugin({
        appendTransaction: (_transactions, oldState, newState) => {
          const { tr } = newState;

          newState.doc.descendants((node, pos) => {
            if (node.type.name !== "paragraph") {
              return;
            }

            const parentIndented = node.attrs.mathIndentation as boolean;

            node.descendants((child, childPos) => {
              if (child.type.name !== "inlineExpression") {
                return;
              }

              const { automaticIndentation } = child.attrs as InlineNodeAttrs;

              if (automaticIndentation !== parentIndented) {
                tr.setNodeMarkup(pos + childPos + 1, child.type, {
                  ...child.attrs,
                  automaticIndentation: parentIndented,
                });
              }
            });
          });

          if (tr.steps.length === 0) {
            return null;
          }

          return tr;
        },
      }),
    ];
  },

  addCommands() {
    return {
      setMathIndentation:
        (mathIndentation) =>
        ({ commands }) => {
          return commands.updateAttributes("paragraph", { mathIndentation });
        },
    };
  },
});
